'use client';

import { useEffect, useState } from 'react';
import { Link2, Copy, Check } from 'lucide-react';
// slugifyStageName, not the never-exported `normalizeSlug`: the bad import was
// undefined at runtime, threw inside the try, and this card silently never
// rendered its URL (found via tsc 2026-07-24).
import { slugifyStageName } from '@/lib/stagename';
import { profileUrl } from '@/lib/seo/canonical';
import DashboardMetricCard from '../DashboardMetricCard';

// "Add JuicyStarz to your bio" — the creator-bio backlink engine. Every creator
// who drops their profile link into a Linktree / X / IG bio is a durable
// backlink to the domain, which is the one scalable off-page authority lever JS
// has (see vault/projects/juicystarz/authority-links-workstream-2026-05-30).
// This card makes copying that link (and an HTML badge for sites that accept it)
// one click from the dashboard.

export default function PromoteProfileCard() {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<'link' | 'badge' | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/profile', { cache: 'no-store', credentials: 'include' });
        const j = res.ok ? await res.json().catch(() => ({})) : {};
        const rawSlug =
          j?.profile?.stageNameNormalized ?? j?.profile?.stageName ?? j?.stageNameNormalized ?? j?.stageName ?? null;
        // Guard on rawSlug, NOT on the slugified result. slugifyStageName falls
        // back to the literal "profile" for null/empty input (stagename.ts:27),
        // so `slug` is always truthy and the !url branch below was unreachable:
        // a creator with no stage name was handed https://juicystarz.com/u/profile,
        // which 404s. Handing out a dead link is worse than showing the
        // finish-your-profile prompt this card already has.
        const slug = rawSlug ? slugifyStageName(rawSlug) : null;
        if (!cancelled) setUrl(slug ? profileUrl(slug) : null);
      } catch {
        if (!cancelled) setUrl(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const copy = async (text: string, which: 'link' | 'badge') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied((c) => (c === which ? null : c)), 2000);
    } catch { /* clipboard blocked — user can still select the text */ }
  };

  const icon = <Link2 className="h-4 w-4" strokeWidth={1.75} />;

  if (loading) {
    return (
      <DashboardMetricCard title="Add to your bio" icon={icon}>
        <div className="space-y-3">
          <div className="h-9 w-full rounded bg-muted animate-pulse" />
          <div className="h-3 w-40 rounded bg-muted/60 animate-pulse" />
        </div>
      </DashboardMetricCard>
    );
  }

  if (!url) {
    return (
      <DashboardMetricCard title="Add to your bio" href="/profile" icon={icon}>
        <div className="text-sm text-muted-foreground">
          Finish your profile to get a shareable link you can add to your Linktree, X, or Instagram bio.
        </div>
      </DashboardMetricCard>
    );
  }

  const badge = `<a href="${url}">Find me on JuicyStarz</a>`;

  return (
    <DashboardMetricCard title="Add to your bio" icon={icon}>
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Drop this link in your Linktree, X, or Instagram bio so fans and bookers can find you.
        </p>

        <div className="flex items-center gap-2">
          <code className="flex-1 truncate rounded border bg-muted/40 px-2 py-1.5 text-xs" title={url}>
            {url.replace(/^https?:\/\//, '')}
          </code>
          <button
            type="button"
            onClick={() => copy(url, 'link')}
            className="inline-flex shrink-0 items-center gap-1 rounded border px-2 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
            aria-label="Copy profile link"
          >
            {copied === 'link'
              ? <><Check className="h-3.5 w-3.5" strokeWidth={1.75} /> Copied</>
              : <><Copy className="h-3.5 w-3.5" strokeWidth={1.75} /> Copy</>}
          </button>
        </div>

        <button
          type="button"
          onClick={() => copy(badge, 'badge')}
          className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
        >
          {copied === 'badge' ? 'Badge HTML copied' : 'Copy HTML badge for your website'}
        </button>
      </div>
    </DashboardMetricCard>
  );
}
